import {
  getSafeCompanySlugFromHostname,
  getSafeCompanySlugFromQuery,
  sanitizeCompanySlug
} from '../utils/companySlug'
import {
  getCanonicalCompanySlug,
  getCompanyProfileBySlug,
  type CompanyProfile,
  type CompanyTheme
} from './companyProfile'

export type SessionUserKey = {
  user_key: string | null
  tenant_id: string | null
}

export type CompanySelection = {
  company_id?: string
  companyId?: string
  slug?: string
  name?: string
  logo?: string
  logoSquare?: string
  tenantId?: string
}

export type AuthRedirectNotice = {
  title: string
  message: string
}

const DEFAULT_COMPANY_API_URL = 'https://companies.bepass.app'
const DEFAULT_AUTH_API_URL = 'https://auth.bepass.app'
const DEFAULT_APPLICATION = 'dash_contingencia'
const DEFAULT_AUTH_CONTEXT = 'dash_contingencia_session'
const SUPPORTED_APPLICATION_IDS = new Set(['2', '3', '7'])
const AUTH_REDIRECT_NOTICE_KEY = 'auth_redirect_notice'
const DEFAULT_COMPANY_SLUG =
  sanitizeCompanySlug(process.env.REACT_APP_DEFAULT_COMPANY_SLUG) || 'botafogo'
const DEFAULT_LOCALHOST_ORIGIN = 'http://localhost:3000'
const DEFAULT_CREDENCIADOS_DEMO_ORIGIN = 'https://bp-demo-credenciados.web.app/'

export const getDefaultApplication = (): string => DEFAULT_APPLICATION

export const getDefaultAuthContext = (): string => DEFAULT_AUTH_CONTEXT

export const hasSupportedApplicationSession = (): boolean => {
  const currentApplicationId = `${window.sessionStorage.getItem('application_id') || ''}`.trim()
  return SUPPORTED_APPLICATION_IDS.has(currentApplicationId)
}

export const getCompanyApiBaseUrl = (): string => DEFAULT_COMPANY_API_URL

export const getAuthApiBaseUrl = (): string => DEFAULT_AUTH_API_URL

export const getAccessOrigin = (): string => {
  const hostname = window.location.hostname.toLowerCase()

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return DEFAULT_LOCALHOST_ORIGIN
  }

  if (hostname.includes('bp-demo-credenciados.web.app')) {
    return DEFAULT_CREDENCIADOS_DEMO_ORIGIN
  }

  return window.location.origin
}

const persistSelectedCompanySlug = (slug: string): string => {
  const canonicalSlug = getCanonicalCompanySlug(slug)
  if (canonicalSlug) {
    const previousSlug =
      sanitizeCompanySlug(window.sessionStorage.getItem('selected_cmp')) ||
      sanitizeCompanySlug(window.sessionStorage.getItem('company_slug'))

    if (previousSlug && previousSlug !== canonicalSlug) {
      window.sessionStorage.removeItem('company_name')
      window.sessionStorage.removeItem('company_logo')
      window.sessionStorage.removeItem('company_logo_square')
      window.sessionStorage.removeItem('company_id')
      window.sessionStorage.removeItem('company')
      window.sessionStorage.removeItem('originCompany')
    }

    window.sessionStorage.setItem('selected_cmp', canonicalSlug)
    window.sessionStorage.setItem('company_slug', canonicalSlug)
  }

  return canonicalSlug
}

const resolveInitialCompanySlug = (): string => {
  const fromQuery = getSafeCompanySlugFromQuery(window.location.search)
  if (fromQuery) return persistSelectedCompanySlug(fromQuery)

  const fromHost = getSafeCompanySlugFromHostname(window.location.hostname)
  if (fromHost) return persistSelectedCompanySlug(fromHost)

  const fromStorage =
    sanitizeCompanySlug(window.sessionStorage.getItem('selected_cmp')) ||
    sanitizeCompanySlug(window.sessionStorage.getItem('company_slug'))
  if (fromStorage) return persistSelectedCompanySlug(fromStorage)

  const fromEnv = sanitizeCompanySlug(process.env.REACT_APP_DEFAULT_COMPANY_SLUG)
  if (fromEnv) return persistSelectedCompanySlug(fromEnv)

  return persistSelectedCompanySlug(DEFAULT_COMPANY_SLUG)
}

export const getStoredUserKey = (): SessionUserKey => {
  const rawValue = window.sessionStorage.getItem('user-key')

  if (!rawValue) {
    return { user_key: null, tenant_id: null }
  }

  try {
    const parsed = JSON.parse(rawValue)
    return {
      user_key: typeof parsed?.user_key === 'string' ? parsed.user_key : null,
      tenant_id:
        typeof parsed?.tenant_id === 'string' ? parsed.tenant_id : null
    }
  } catch {
    return { user_key: null, tenant_id: null }
  }
}

export const getCurrentTenantId = (): string => {
  const storedTenant =
    window.sessionStorage.getItem('tenant') || getStoredUserKey().tenant_id || ''
  const profileTenant = getCurrentCompanyProfile().tenantId || ''
  const companySlug =
    window.sessionStorage.getItem('company_slug') ||
    window.sessionStorage.getItem('selected_cmp') ||
    ''

  return `${storedTenant || profileTenant || companySlug}`.trim()
}

export const getCurrentApiKey = (): string => {
  const operationalApiKey = `${window.sessionStorage.getItem('operational_api_key') || ''}`.trim()
  if (operationalApiKey) return operationalApiKey

  const storedUserKey = `${getStoredUserKey().user_key || ''}`.trim()
  if (storedUserKey) return storedUserKey

  return `${window.sessionStorage.getItem('token') || ''}`.trim()
}

const decodeJwtPayload = (token: string): Record<string, any> | null => {
  const normalizedToken = `${token || ''}`.trim()

  if (!normalizedToken) {
    return null
  }

  const tokenParts = normalizedToken.split('.')

  if (tokenParts.length < 2) {
    return null
  }

  try {
    const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    )
    const decodedPayload = window.atob(paddedBase64)

    return JSON.parse(decodedPayload)
  } catch {
    return null
  }
}

export const getCurrentApiKeyExpirationMs = (): number | null => {
  const payload = decodeJwtPayload(getCurrentApiKey())

  if (!payload || typeof payload.exp !== 'number') {
    return null
  }

  return payload.exp * 1000
}

export const isCurrentApiKeyUsable = (): boolean => {
  if (!getCurrentApiKey()) {
    return false
  }

  const expirationMs = getCurrentApiKeyExpirationMs()

  if (!expirationMs) {
    return false
  }

  const currentEpochInSeconds = Math.floor(Date.now() / 1000)

  return expirationMs > currentEpochInSeconds * 1000
}

export const getSelectedCompanySlug = (): string => resolveInitialCompanySlug()

export const getCurrentCompanyProfile = (): CompanyProfile =>
  getCompanyProfileBySlug(getSelectedCompanySlug())

export const getCurrentCompanyTheme = (): CompanyTheme =>
  getCurrentCompanyProfile().theme

export const getDashboardApiBaseUrl = (): string =>
  getCurrentCompanyProfile().dashboardApiBaseUrl

export const getUserApiBaseUrl = (): string =>
  getCurrentCompanyProfile().userApiBaseUrl

export const getEventApiBaseUrl = (): string =>
  getCurrentCompanyProfile().eventApiBaseUrl

export const getTicketSearchPath = (): string =>
  getCurrentCompanyProfile().ticketSearchPath

export const getTicketSearchWithSessionsPath = (): string =>
  getCurrentCompanyProfile().ticketSearchWithSessionsPath

export const applySelectedCompany = (company: CompanySelection): void => {
  const companyId = company.company_id || company.companyId || ''
  const tenantId = `${company.tenantId || ''}`.trim()
  const slug = getCanonicalCompanySlug(company.slug) || ''

  if (slug) {
    persistSelectedCompanySlug(slug)
  }

  if (tenantId) {
    window.sessionStorage.setItem('tenant', tenantId)
    window.sessionStorage.setItem(
      'user-key',
      JSON.stringify({
        user_key: getStoredUserKey().user_key,
        tenant_id: tenantId
      })
    )
  }

  if (companyId) {
    window.sessionStorage.setItem('company_id', companyId)
    window.sessionStorage.setItem('company', companyId)
    window.sessionStorage.setItem('originCompany', companyId)
  }

  if (company.name) window.sessionStorage.setItem('company_name', company.name)
  if (company.logo) window.sessionStorage.setItem('company_logo', company.logo)
  if (company.logoSquare) {
    window.sessionStorage.setItem('company_logo_square', company.logoSquare)
  }
}

export const storeAuthenticatedSession = (params: {
  apiKey: string
  refreshToken?: string
  tenantId: string
  companyId: string
  role: string
  roleId?: string
  loginId?: string
  name?: string
}): void => {
  const {
    apiKey,
    refreshToken,
    tenantId,
    companyId,
    role,
    roleId,
    loginId,
    name
  } = params

  window.sessionStorage.setItem('auth', 'true')
  window.sessionStorage.setItem('token', apiKey)
  window.sessionStorage.setItem(
    'user-key',
    JSON.stringify({ user_key: apiKey, tenant_id: tenantId })
  )
  window.sessionStorage.setItem('tenant', tenantId)
  window.sessionStorage.setItem('operational_api_key', apiKey)
  window.sessionStorage.setItem('company_id', companyId)
  window.sessionStorage.setItem('company', companyId)
  window.sessionStorage.setItem('originCompany', companyId)
  window.sessionStorage.setItem('role', role)
  window.sessionStorage.setItem('application', DEFAULT_APPLICATION)
  window.sessionStorage.setItem('x-application', DEFAULT_APPLICATION)
  window.sessionStorage.setItem('auth_context', DEFAULT_AUTH_CONTEXT)
  if (roleId) window.sessionStorage.setItem('role_id', roleId)
  if (loginId) window.sessionStorage.setItem('login_id', loginId)
  if (name) window.sessionStorage.setItem('name', name)

  if (refreshToken) {
    window.sessionStorage.setItem('refreshToken', refreshToken)
  } else {
    window.sessionStorage.removeItem('refreshToken')
  }
}

export const getDefaultRequestHeaders = (
  additionalHeaders: Record<string, string> = {}
): Record<string, string> => {
  const apiKey = getCurrentApiKey()
  const tenantId = getCurrentTenantId()
  const loginId = `${window.sessionStorage.getItem('login_id') || ''}`.trim()
  const companyId =
    `${window.sessionStorage.getItem('company_id') || window.sessionStorage.getItem('company') || window.sessionStorage.getItem('originCompany') || ''}`.trim()

  return {
    ...(apiKey ? { 'x-api-key': apiKey } : {}),
    ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
    ...(companyId ? { 'x-company-id': companyId } : {}),
    ...(loginId ? { 'x-login-id': loginId } : {}),
    'x-application': DEFAULT_APPLICATION,
    ...additionalHeaders
  }
}

export const hasAuthenticatedSession = (): boolean => {
  return (
    window.sessionStorage.getItem('auth') === 'true' &&
    (
      window.sessionStorage.getItem('x-application') === DEFAULT_APPLICATION ||
      hasSupportedApplicationSession()
    ) &&
    Boolean(getCurrentTenantId()) &&
    Boolean(getCurrentApiKey())
  )
}

export const hasUsableAuthenticatedSession = (): boolean =>
  hasAuthenticatedSession() && isCurrentApiKeyUsable()

export const clearAuthSession = (): void => {
  const selectedCmp = window.sessionStorage.getItem('selected_cmp')
  const companySlug = window.sessionStorage.getItem('company_slug')

  window.sessionStorage.clear()

  if (selectedCmp) window.sessionStorage.setItem('selected_cmp', selectedCmp)
  if (companySlug) window.sessionStorage.setItem('company_slug', companySlug)
}

export const storeAuthRedirectNotice = (
  notice: AuthRedirectNotice
): void => {
  window.sessionStorage.setItem(
    AUTH_REDIRECT_NOTICE_KEY,
    JSON.stringify(notice)
  )
}

export const consumeAuthRedirectNotice = (): AuthRedirectNotice | null => {
  const rawNotice = window.sessionStorage.getItem(AUTH_REDIRECT_NOTICE_KEY)

  if (!rawNotice) {
    return null
  }

  window.sessionStorage.removeItem(AUTH_REDIRECT_NOTICE_KEY)

  try {
    const parsedNotice = JSON.parse(rawNotice)

    if (
      typeof parsedNotice?.title === 'string' &&
      typeof parsedNotice?.message === 'string'
    ) {
      return {
        title: parsedNotice.title,
        message: parsedNotice.message
      }
    }
  } catch {
    return null
  }

  return null
}

export const getCompanyPortalOrigin = (): string => {
  const explicitOrigin = `${window.sessionStorage.getItem('company_origin') || ''}`.trim()
  if (explicitOrigin) return explicitOrigin

  return getCurrentCompanyProfile().portalOrigin || window.location.origin
}

export const getCompanyBrandName = (): string => {
  const storedName = `${window.sessionStorage.getItem('company_name') || ''}`.trim()
  if (storedName) return storedName

  return getCurrentCompanyProfile().brandName
}

export const getCompanyLogoUrl = (): string => {
  const storedLogo = `${window.sessionStorage.getItem('company_logo_square') || window.sessionStorage.getItem('company_logo') || ''}`.trim()
  if (storedLogo) return storedLogo

  return `${getCurrentCompanyProfile().logoUrl || ''}`.trim()
}

export const applyCurrentCompanyTheme = (): void => {
  const slug = getSelectedCompanySlug()
  const theme = getCurrentCompanyTheme()
  const rootStyle = document.documentElement.style

  rootStyle.setProperty('--club-primary', theme.primary)
  rootStyle.setProperty('--club-primary-contrast', theme.primaryContrast)
  rootStyle.setProperty('--club-accent', theme.accent)
  rootStyle.setProperty('--club-accent-soft', theme.accentSoft)
  rootStyle.setProperty('--club-sidebar', theme.sidebar)
  rootStyle.setProperty('--club-header', theme.header)
  rootStyle.setProperty('--club-surface', theme.surface)
  rootStyle.setProperty('--club-surface-alt', theme.surfaceAlt)
  rootStyle.setProperty('--club-text', theme.text)
  rootStyle.setProperty('--club-text-muted', theme.textMuted)
  document.body.dataset.club = slug || 'default'
}
